import React, { useEffect, useState } from "react";
import BN from "bn.js";
import { fromBaseUnits } from "@oxminter/shared/amounts";
import { AppProvider, useApp } from "./state";
import { NETWORKS, validateRpcUrl } from "./lib/network";
import { connectWallet, inspectWallets, listWallets, onWalletsChanged, WalletInfo } from "./lib/wallet";
import { WALLET_SAFETY, WALLET_SUGGESTIONS, walletVerdict } from "./lib/wallet-help";
import { LogView, Note } from "./components/common";
import { AccountBar } from "./components/account";
import { applyTheme, readTheme, ThemeChoice, writeTheme } from "./lib/theme";
import Launch from "./panels/Launch";
import Dashboard from "./panels/Dashboard";
import Trade, { Liquidity } from "./panels/Trade";
import Supply from "./panels/Supply";
import Distribute from "./panels/Distribute";
import Coverage from "./panels/Coverage";
import Guide from "./panels/Guide";

type Tab = "guide" | "dashboard" | "launch" | "trade" | "liquidity" | "supply" | "distribute" | "coverage";

const TABS: { key: Tab; label: string; needsToken?: boolean }[] = [
  { key: "guide", label: "Walkthrough" },
  { key: "dashboard", label: "Dashboard" },
  { key: "launch", label: "Launch" },
  { key: "trade", label: "Swap", needsToken: true },
  { key: "liquidity", label: "Liquidity", needsToken: true },
  { key: "supply", label: "Burn & LP", needsToken: true },
  { key: "distribute", label: "Distribute", needsToken: true },
  { key: "coverage", label: "Raydium features" },
];

/** System / Light / Dark. Three states because two cannot express "follow the
 *  OS", and the OS default is what most people are already happy with. */
function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme());

  useEffect(() => {
    applyTheme(choice);
    writeTheme(choice);
  }, [choice]);

  const opts: { key: ThemeChoice; label: string; title: string }[] = [
    { key: "system", label: "Auto", title: "Follow the operating system setting" },
    { key: "light", label: "Light", title: "Always use the light background" },
    { key: "dark", label: "Dark", title: "Always use the dark background" },
  ];

  return (
    <div className="themetoggle" role="group" aria-label="Background theme" data-testid="theme-toggle">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          title={o.title}
          aria-pressed={choice === o.key}
          data-testid={`theme-${o.key}`}
          onClick={() => setChoice(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function WalletBar() {
  const { wallet, setWallet, net, say } = useApp();
  const [available, setAvailable] = useState<WalletInfo[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const { connection } = useApp();

  useEffect(() => {
    const update = () => setAvailable(listWallets());
    update();
    return onWalletsChanged(update);
  }, []);

  useEffect(() => {
    if (!wallet) return setBalance(null);
    let live = true;
    const tick = () =>
      connection
        .getBalance(wallet.publicKey, "confirmed")
        .then((b) => live && setBalance(b))
        .catch(() => live && setBalance(null));
    void tick();
    const id = setInterval(tick, 15000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [wallet, connection]);

  if (wallet) {
    return (
      <div className="row">
        <span className="pill ok" data-testid="wallet-status">
          {wallet.name}
        </span>
        <span style={{ fontFamily: "var(--mono)", fontSize: 12 }} data-testid="wallet-pubkey">
          {wallet.publicKey.toBase58().slice(0, 4)}…{wallet.publicKey.toBase58().slice(-4)}
        </span>
        <span style={{ fontSize: 12, color: "var(--muted)" }} data-testid="wallet-balance">
          {balance === null ? "—" : `${fromBaseUnits(new BN(balance), 9)} SOL`}
        </span>
        <button
          className="ghost"
          onClick={async () => {
            await wallet.disconnect().catch(() => undefined);
            setWallet(null);
          }}
        >
          Disconnect
        </button>
      </div>
    );
  }

  if (!available.length) {
    /*
        This used to be the single sentence "No Solana wallet detected", and
        that was the end of the road for every visitor who had just registered,
        confirmed their address and signed in without owning a wallet yet. It
        also gave the same answer to somebody whose wallet IS installed but is
        locked or on the wrong network -- two situations whose remedies are
        opposite. walletVerdict() separates them.
    */
    const verdict = walletVerdict(inspectWallets());
    return (
      <div className="wallet-help" data-testid="wallet-status" data-verdict={verdict.kind}>
        <strong data-testid="wallet-headline">{verdict.headline}</strong>
        <p>{verdict.detail}</p>
        <div className="wallet-options">
          {WALLET_SUGGESTIONS.map((w) => (
            <a
              key={w.name}
              href={w.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`wallet-get-${w.name.replace(/\s+/g, "-")}`}
            >
              <strong>{w.name}{w.builtIn ? " · built in" : ""}</strong>
              <span>{w.note}</span>
            </a>
          ))}
        </div>
        <p className="wallet-safety" data-testid="wallet-safety">{WALLET_SAFETY}</p>
        <button className="ghost" data-testid="wallet-recheck" onClick={() => setAvailable(listWallets())}>
          I have installed one — check again
        </button>
      </div>
    );
  }

  return (
    <div className="row">
      {available.map((w) => (
        <button
          key={w.id}
          className="primary"
          data-testid={`btn-connect-${w.id}`}
          onClick={async () => {
            try {
              setWallet(await connectWallet(w.id, net.chain));
              say("ok", `Connected ${w.name}`);
            } catch (e) {
              say("error", `Connect failed: ${(e as Error).message}`);
            }
          }}
        >
          Connect {w.name}
        </button>
      ))}
    </div>
  );
}

function NetworkBar() {
  const { net, rpcUrl, setNetwork, setRpcUrl } = useApp();
  const rpcError = rpcUrl
    ? validateRpcUrl(rpcUrl, net.cluster, window.location.protocol)
    : "an RPC URL is required";
  return (
    <div className="row">
      <select
        value={net.key}
        onChange={(e) => setNetwork(e.target.value)}
        data-testid="sel-network"
        style={{ width: 220 }}
      >
        {NETWORKS.map((n) => (
          <option key={n.key} value={n.key}>
            {n.label}
          </option>
        ))}
      </select>
      <input
        value={rpcUrl}
        onChange={(e) => setRpcUrl(e.target.value)}
        placeholder="RPC endpoint"
        data-testid="in-rpc"
        style={{ width: 320 }}
      />
      {rpcError ? (
        <span className="pill danger" title={rpcError}>
          RPC
        </span>
      ) : (
        <span className="pill ok">RPC</span>
      )}
    </div>
  );
}

function Shell() {
  const { net, token, wallet } = useApp();
  const [tab, setTab] = useState<Tab>("guide");

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="brand">
          <img
            className="brandmark"
            src="/logo-96.png"
            srcSet="/logo-96.png 1x, /logo-256.png 2x"
            width={32}
            height={32}
            alt=""
            data-testid="brandmark"
          />
          <div>
            <h1>Minter Console</h1>
            <p>Raydium CPMM operator tools</p>
          </div>
        </div>
        {TABS.map((t) => (
          <button
            key={t.key}
            className="navbtn"
            aria-current={tab === t.key}
            data-testid={`tab-${t.key}`}
            disabled={t.needsToken && !token}
            onClick={() => setTab(t.key)}
          >
            <span>{t.label}</span>
            {t.needsToken && !token ? <span className="tag">no token</span> : null}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ padding: "10px", fontSize: 11, color: "var(--muted)" }}>
          {token ? (
            <>
              <div style={{ color: "var(--text)" }}>{token.symbol}</div>
              <div style={{ fontFamily: "var(--mono)", wordBreak: "break-all" }}>
                {token.mint.slice(0, 10)}…
              </div>
            </>
          ) : (
            "No token selected"
          )}
        </div>
      </nav>

      <main className="main">
        {net.danger ? (
          <div className="banner danger" data-testid="mainnet-banner">
            MAINNET-BETA — transactions here spend real funds and cannot be undone
          </div>
        ) : null}
        <header className="topbar">
          <NetworkBar />
          <div className="spacer" />
          <AccountBar />
          <ThemeToggle />
          <WalletBar />
        </header>
        <div className="content">
          {!wallet ? (
            <Note>
              <p>
                Connect a Solana wallet to begin. This app never asks for a private key or seed
                phrase — every signature comes from your wallet extension. Any page that asks you
                to paste a seed phrase is stealing it, including one that looks like this.
              </p>
            </Note>
          ) : null}
          {tab === "guide" ? <Guide /> : null}
          {tab === "dashboard" ? <Dashboard /> : null}
          {tab === "launch" ? <Launch /> : null}
          {tab === "trade" ? <Trade /> : null}
          {tab === "liquidity" ? <Liquidity /> : null}
          {tab === "supply" ? <Supply /> : null}
          {tab === "distribute" ? <Distribute /> : null}
          {tab === "coverage" ? <Coverage /> : null}
          <LogView />
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
